import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { RefreshCw, Clock, MessageSquare, User, Building2, Send, Lock, X, Bell } from 'lucide-react';
import { helpAPI } from '../api/client';

const LS_LAST_SEEN_KEY = 'spocodoc_tickets_last_seen';

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────
const PRIORITY_BADGE = {
  Low:    'bg-gray-100 text-gray-600',
  Normal: 'bg-blue-100 text-blue-700',
  High:   'bg-amber-100 text-amber-700',
  Urgent: 'bg-red-100 text-red-700',
};
const STATUS_BADGE = {
  Open:          'bg-blue-100 text-blue-800',
  'In Progress': 'bg-amber-100 text-amber-800',
  Resolved:      'bg-emerald-100 text-emerald-800',
  Closed:        'bg-gray-200 text-gray-600',
};
const SLA_HOURS = { Urgent: 4, High: 8, Normal: 24, Low: 72 };

function slaInfo(ticket) {
  if (!ticket.SLADueAt || ['Resolved', 'Closed'].includes(ticket.Status)) return null;
  const msLeft = new Date(ticket.SLADueAt) - Date.now();
  const hLeft  = msLeft / 3600000;
  const breached = msLeft < 0;
  const atRisk   = !breached && hLeft < (SLA_HOURS[ticket.Priority] || 24) * 0.25;
  return {
    breached,
    atRisk,
    label: breached
      ? `SLA breached ${Math.abs(Math.round(hLeft))}h ago`
      : hLeft < 1
        ? `SLA: ${Math.round(msLeft / 60000)}m left`
        : `SLA: ${Math.round(hLeft)}h left`,
    color: breached ? 'text-red-600' : atRisk ? 'text-amber-600' : 'text-emerald-600',
  };
}

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso);
  if (ms < 60000)    return 'just now';
  if (ms < 3600000)  return `${Math.round(ms / 60000)}m ago`;
  if (ms < 86400000) return `${Math.round(ms / 3600000)}h ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// ────────────────────────────────────────────────────────────────────────────
// Inner component: a single chat bubble
// ────────────────────────────────────────────────────────────────────────────
function MessageBubble({ name, date, body, label, labelClass, labelIcon, bubbleClass, avatarClass, dimmed }) {
  return (
    <div className={`flex gap-3 ${dimmed ? 'opacity-85' : ''}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${avatarClass}`}>
        {(name || '?')[0].toUpperCase()}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-sm font-semibold text-gray-900">{name}</span>
          <span className="text-xs text-gray-400">{timeAgo(date)}</span>
          {label && (
            <span className={`text-xs px-1.5 py-0.5 rounded flex items-center gap-1 font-medium ${labelClass}`}>
              {labelIcon}{label}
            </span>
          )}
        </div>
        <div className={`rounded-lg p-3 text-sm whitespace-pre-wrap border ${bubbleClass}`}>
          {body}
        </div>
      </div>
    </div>
  );
}

export default function SupportTicketsAdmin() {
  const [tickets,        setTickets]        = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [statusFilter,   setStatusFilter]   = useState('Open');
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [companyFilter,  setCompanyFilter]  = useState('All');
  const [selected,       setSelected]       = useState(null);
  const [messages,       setMessages]       = useState([]);
  const [msgLoading,     setMsgLoading]     = useState(false);
  const [replyBody,      setReplyBody]      = useState('');
  const [isInternal,     setIsInternal]     = useState(false);
  const [sending,        setSending]        = useState(false);
  const [newStatus,      setNewStatus]      = useState('');
  const [resolutionNotes,setResolutionNotes]= useState('');
  const [saving,         setSaving]         = useState(false);
  const [newTicketBanner, setNewTicketBanner] = useState(null); // { count, newestAt }
  const threadRef = useRef(null);

  const scrollThread = () =>
    setTimeout(() => threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' }), 80);

  // Unique companies from loaded ticket list (for the company dropdown)
  const companyOptions = useMemo(() => {
    const map = new Map();
    tickets.forEach(t => {
      if (t.CompanyID && t.CompanyName) map.set(t.CompanyID, t.CompanyName);
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [tickets]);

  // Derive client-side filtered list (company filter applied locally so dropdown
  // stays populated regardless of what's selected)
  const filteredTickets = useMemo(() => {
    if (companyFilter === 'All') return tickets;
    return tickets.filter(t => t.CompanyID === companyFilter);
  }, [tickets, companyFilter]);

  // Load ticket list
  const loadTickets = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    helpAPI.listTickets({
      status:   statusFilter   === 'All' ? undefined : statusFilter,
      priority: priorityFilter === 'All' ? undefined : priorityFilter,
    }).then(r => {
      setTickets(r.data);
      // Check for new tickets the user hasn't seen yet
      const lastSeen = localStorage.getItem(LS_LAST_SEEN_KEY);
      const lastSeenMs = lastSeen ? new Date(lastSeen).getTime() : 0;
      const newOnes = r.data.filter(t =>
        t.Status === 'Open' && new Date(t.CreatedAt).getTime() > lastSeenMs
      );
      if (newOnes.length > 0) {
        const newest = newOnes.reduce((a, b) =>
          new Date(a.CreatedAt) > new Date(b.CreatedAt) ? a : b
        );
        setNewTicketBanner({ count: newOnes.length, newestAt: newest.CreatedAt });
      }
    }).finally(() => setLoading(false));
  }, [statusFilter, priorityFilter]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  // Auto-poll for new tickets every 60s (silent refresh)
  useEffect(() => {
    const interval = setInterval(() => loadTickets(true), 60000);
    return () => clearInterval(interval);
  }, [loadTickets]);

  const dismissBanner = () => {
    localStorage.setItem(LS_LAST_SEEN_KEY, new Date().toISOString());
    setNewTicketBanner(null);
  };

  // Open ticket detail + thread
  const openTicket = async (t) => {
    setMsgLoading(true);
    try {
      const [ticketRes, msgsRes] = await Promise.all([
        helpAPI.getTicket(t.TicketID),
        helpAPI.getMessages(t.TicketID),
      ]);
      setSelected(ticketRes.data);
      setMessages(msgsRes.data);
      setNewStatus(ticketRes.data.Status);
      setResolutionNotes(ticketRes.data.ResolutionNotes || '');
      setReplyBody('');
      setIsInternal(false);
      scrollThread();
    } finally {
      setMsgLoading(false);
    }
  };

  // Send reply or internal note
  const sendReply = async () => {
    if (!replyBody.trim()) return;
    setSending(true);
    try {
      await helpAPI.addMessage(selected.TicketID, replyBody, isInternal);
      const [ticketRes, msgsRes] = await Promise.all([
        helpAPI.getTicket(selected.TicketID),
        helpAPI.getMessages(selected.TicketID),
      ]);
      setSelected(ticketRes.data);
      setMessages(msgsRes.data);
      setNewStatus(ticketRes.data.Status);
      setReplyBody('');
      loadTickets();
      scrollThread();
    } finally {
      setSending(false);
    }
  };

  // Save status / resolution notes
  const saveTicket = async () => {
    setSaving(true);
    try {
      await helpAPI.updateTicket(selected.TicketID, { status: newStatus, resolutionNotes });
      const r = await helpAPI.getTicket(selected.TicketID);
      setSelected(r.data);
      setNewStatus(r.data.Status);
      loadTickets();
    } finally {
      setSaving(false);
    }
  };

  const sla     = selected ? slaInfo(selected) : null;
  const isClosed = selected && ['Resolved', 'Closed'].includes(selected.Status);

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] overflow-hidden">

      {/* ── New ticket banner ────────────────────────────────────────────── */}
      {newTicketBanner && (
        <div className="flex-shrink-0 flex items-center gap-3 bg-blue-600 text-white px-5 py-2.5 text-sm">
          <Bell className="w-4 h-4 flex-shrink-0 animate-pulse" />
          <span className="flex-1 font-medium">
            {newTicketBanner.count === 1
              ? `1 new support ticket arrived — ${timeAgo(newTicketBanner.newestAt)}`
              : `${newTicketBanner.count} new support tickets arrived — latest ${timeAgo(newTicketBanner.newestAt)}`}
          </span>
          <button
            onClick={() => { setStatusFilter('Open'); setCompanyFilter('All'); dismissBanner(); }}
            className="underline font-semibold text-white hover:text-blue-100 text-sm whitespace-nowrap">
            View open tickets
          </button>
          <button onClick={dismissBanner} className="ml-1 text-white/70 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">

      {/* ── Left panel: ticket list ─────────────────────────────────────── */}
      <div className="w-80 flex-shrink-0 border-r border-gray-200 flex flex-col bg-gray-50">

        {/* Header + filters */}
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h1 className="font-bold text-gray-900 text-base">Support Tickets</h1>
            <button onClick={loadTickets} title="Refresh"
              className="text-gray-400 hover:text-gray-700 p-1 rounded">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-2">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="w-full border rounded-lg px-2 py-1.5 text-sm bg-white outline-none focus:ring-2 focus:ring-blue-300">
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="Resolved">Resolved</option>
              <option value="Closed">Closed</option>
              <option value="All">All statuses</option>
            </select>
            <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}
              className="w-full border rounded-lg px-2 py-1.5 text-sm bg-white outline-none focus:ring-2 focus:ring-blue-300">
              <option value="All">All priorities</option>
              <option value="Urgent">🔴 Urgent</option>
              <option value="High">🟠 High</option>
              <option value="Normal">🔵 Normal</option>
              <option value="Low">⚪ Low</option>
            </select>
            <select value={companyFilter} onChange={e => setCompanyFilter(e.target.value)}
              className="w-full border rounded-lg px-2 py-1.5 text-sm bg-white outline-none focus:ring-2 focus:ring-blue-300">
              <option value="All">🏢 All companies</option>
              {companyOptions.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Ticket rows */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-gray-400 text-sm">Loading…</div>
          ) : filteredTickets.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-gray-400 text-sm">No tickets found</div>
          ) : filteredTickets.map(t => {
            const s = slaInfo(t);
            const isActive = selected?.TicketID === t.TicketID;
            return (
              <button key={t.TicketID} onClick={() => openTicket(t)}
                className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-white transition-colors
                  ${isActive ? 'bg-white border-l-4 border-l-blue-500 pl-3' : ''}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${PRIORITY_BADGE[t.Priority] || 'bg-gray-100 text-gray-600'}`}>
                        {t.Priority}
                      </span>
                      <span className="text-xs text-gray-400 font-mono">#{t.TicketNumber}</span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 truncate">{t.Subject}</p>
                    <p className="text-xs text-gray-500 truncate">{t.UserName || t.Email}</p>
                    {t.CompanyName && <p className="text-xs text-gray-400 truncate">{t.CompanyName}</p>}
                    {s && <p className={`text-xs font-semibold mt-0.5 ${s.color}`}>{s.label}</p>}
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${STATUS_BADGE[t.Status] || 'bg-gray-100'}`}>
                      {t.Status}
                    </span>
                    <p className="text-xs text-gray-400 mt-1">{timeAgo(t.CreatedAt)}</p>
                    {t.MessageCount > 0 && (
                      <p className="text-xs text-blue-500 mt-0.5">💬 {t.MessageCount}</p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Right panel: thread view ────────────────────────────────────── */}
      {!selected ? (
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center text-gray-400">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Select a ticket to view the conversation</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col overflow-hidden bg-white">

          {/* Ticket header */}
          <div className="border-b border-gray-200 p-4 flex-shrink-0">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-gray-400 font-mono">#{selected.TicketNumber}</span>
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${PRIORITY_BADGE[selected.Priority] || ''}`}>
                    {selected.Priority}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[selected.Status] || ''}`}>
                    {selected.Status}
                  </span>
                  {sla && (
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${
                      sla.breached ? 'bg-red-50 border-red-200 text-red-700' :
                      sla.atRisk   ? 'bg-amber-50 border-amber-200 text-amber-700' :
                                     'bg-emerald-50 border-emerald-200 text-emerald-700'
                    }`}>{sla.label}</span>
                  )}
                </div>
                <h2 className="font-bold text-gray-900 text-lg mt-1 truncate">{selected.Subject}</h2>
                <div className="flex items-center gap-3 mt-0.5 text-sm text-gray-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <User className="w-3.5 h-3.5" />{selected.UserName || selected.Email}
                  </span>
                  {selected.CompanyName && (
                    <span className="flex items-center gap-1">
                      <Building2 className="w-3.5 h-3.5" />{selected.CompanyName}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {new Date(selected.CreatedAt).toLocaleString('en-GB')}
                  </span>
                  {selected.Category && (
                    <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{selected.Category}</span>
                  )}
                </div>
                {selected.PageUrl && (
                  <p className="text-xs text-gray-400 mt-0.5 truncate">Raised on: {selected.PageUrl}</p>
                )}
              </div>

              {/* Status + Save */}
              <div className="flex-shrink-0 flex items-center gap-2">
                <select value={newStatus} onChange={e => setNewStatus(e.target.value)}
                  className="border rounded-lg px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-300">
                  <option>Open</option>
                  <option>In Progress</option>
                  <option>Resolved</option>
                  <option>Closed</option>
                </select>
                <button onClick={saveTicket} disabled={saving}
                  className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60 whitespace-nowrap">
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>

            {/* Resolution notes — admin-only, collapsible */}
            <details className="mt-3">
              <summary className="text-xs text-gray-500 cursor-pointer select-none hover:text-gray-700 w-fit">
                Internal resolution notes (not visible to customer)
              </summary>
              <textarea
                value={resolutionNotes}
                onChange={e => setResolutionNotes(e.target.value)}
                placeholder="Private notes for your team…"
                className="w-full border border-amber-200 rounded-lg px-2 py-2 mt-2 text-sm h-16 bg-amber-50 text-gray-700 resize-none outline-none focus:ring-2 focus:ring-amber-300"
              />
            </details>
          </div>

          {/* Thread */}
          <div ref={threadRef} className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Original message */}
            <MessageBubble
              name={selected.UserName || selected.Email}
              date={selected.CreatedAt}
              body={selected.Body}
              label="Original message"
              labelClass="bg-blue-100 text-blue-700"
              bubbleClass="bg-gray-50 border-gray-200"
              avatarClass="bg-blue-100 text-blue-700"
            />

            {msgLoading ? (
              <div className="text-center text-gray-400 text-sm py-4">Loading conversation…</div>
            ) : messages.map(m => {
              const internal = m.IsInternal === true || m.IsInternal === 1;
              const name = m.UserFullName || m.AuthorName || m.AuthorEmail || m.UserEmail || 'Support';
              const isAgent = m.AuthorEmail && m.AuthorEmail !== selected.Email;
              return (
                <MessageBubble
                  key={m.MessageID}
                  name={name}
                  date={m.CreatedAt}
                  body={m.Body}
                  label={internal ? 'Internal note' : isAgent ? 'Support' : null}
                  labelClass={internal ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'}
                  labelIcon={internal ? <Lock className="w-3 h-3" /> : null}
                  bubbleClass={internal
                    ? 'bg-amber-50 border-amber-200 text-amber-900'
                    : isAgent
                      ? 'bg-indigo-50 border-indigo-100'
                      : 'bg-gray-50 border-gray-200'}
                  avatarClass={internal
                    ? 'bg-amber-100 text-amber-700'
                    : isAgent
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-blue-100 text-blue-700'}
                  dimmed={internal}
                />
              );
            })}
          </div>

          {/* Reply box */}
          {isClosed ? (
            <div className="border-t border-gray-200 p-4 bg-emerald-50 text-sm text-emerald-700 text-center flex-shrink-0">
              Ticket is <strong>{selected.Status}</strong>. Change status above and save to re-open.
            </div>
          ) : (
            <div className="border-t border-gray-200 p-4 flex-shrink-0 bg-white">
              {/* Reply type tabs */}
              <div className="flex gap-2 mb-2">
                <button onClick={() => setIsInternal(false)}
                  className={`text-xs px-2.5 py-1 rounded font-medium transition-colors
                    ${!isInternal ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  💬 Public reply
                </button>
                <button onClick={() => setIsInternal(true)}
                  className={`text-xs px-2.5 py-1 rounded font-medium flex items-center gap-1 transition-colors
                    ${isInternal ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  <Lock className="w-3 h-3" /> Internal note
                </button>
              </div>

              <div className={`border rounded-lg overflow-hidden ${isInternal ? 'border-amber-300' : 'border-gray-200'}`}>
                <textarea
                  value={replyBody}
                  onChange={e => setReplyBody(e.target.value)}
                  placeholder={isInternal
                    ? 'Internal note — only visible to the support team…'
                    : 'Type a reply to the customer… (they will be notified by email)'}
                  rows={3}
                  className={`w-full px-3 py-2 text-sm resize-none outline-none
                    ${isInternal ? 'bg-amber-50 text-amber-900 placeholder:text-amber-400' : 'bg-white'}`}
                  onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) sendReply(); }}
                />
                <div className={`flex items-center justify-between px-3 py-2 border-t
                  ${isInternal ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100'}`}>
                  <span className="text-xs text-gray-400">Ctrl+Enter to send</span>
                  <button onClick={sendReply} disabled={sending || !replyBody.trim()}
                    className="inline-flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-opacity">
                    <Send className="w-3.5 h-3.5" />
                    {sending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </div>

              <p className="text-xs text-gray-400 mt-1.5">
                {isInternal
                  ? 'This note is only visible to the support team.'
                  : 'Customer will be notified by email.'}
              </p>
            </div>
          )}
        </div>
      )}
      </div>{/* end inner flex row */}
    </div>
  );
}
